import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

const MOBILE_COLLECTION_MQ = window.matchMedia('(max-width: 989px)');

/**
 * Collection fetch URL with Shopify filter params (not pretty /f/ display URLs).
 * @returns {URL}
 */
function getJudyCollectionFetchUrl() {
  const state = history.state;
  if (state && typeof state.fetchUrl === 'string' && state.fetchUrl) {
    return new URL(state.fetchUrl, window.location.origin);
  }

  const filters = document.querySelector('collection-judy-filters');
  if (filters && typeof filters.resolveLocationToUrls === 'function') {
    const { fetchUrl } = filters.resolveLocationToUrls();
    if (fetchUrl) {
      return new URL(fetchUrl, window.location.origin);
    }
  }

  const url = new URL(window.location.href);
  const subHandle = url.searchParams.get('judy_sub_collection');
  if (subHandle) {
    const filters = document.querySelector('collection-judy-filters');
    const parentPath =
      filters?.dataset.allRingsUrl?.trim().split('?')[0].replace(/\/+$/, '') ||
      url.pathname.replace(/\/+$/, '');
    const parentHandle = parentPath.match(/\/collections\/([^/?#]+)/i)?.[1];
    const subUrl = new URL(`/collections/${encodeURIComponent(subHandle)}`, window.location.origin);
    if (parentHandle) {
      subUrl.searchParams.set('judy_parent_collection', parentHandle);
    }
    return subUrl;
  }

  url.hash = '';
  return url;
}

/**
 * Reveal an appended Judy product batch only when it reaches the visible
 * collection viewport. Starting this at append time makes the animation finish
 * while the cards are still below the fold.
 *
 * @param {HTMLElement} grid
 * @param {HTMLElement[]} items
 */
function revealJudyAppendedItems(grid, items) {
  if (!items.length) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const reveal = () => {
    items.forEach((item, index) => {
      window.setTimeout(
        () => {
          if (!item.isConnected) return;
          item.classList.add('is-append-revealed');
        },
        reducedMotion ? 0 : index * 70
      );
    });
  };

  if (reducedMotion || !('IntersectionObserver' in window)) {
    reveal();
    return;
  }

  const mobileScroller =
    grid.dataset.layout === 'judy-mobile'
      ? document.querySelector('[data-judy-collection-scroller]')
      : null;
  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      reveal();
    },
    {
      root: mobileScroller instanceof HTMLElement ? mobileScroller : null,
      rootMargin: '0px 0px 10% 0px',
      threshold: 0.01,
    }
  );

  observer.observe(items[0]);
}

/**
 * Apply the same viewport-triggered sequence to the first mobile batch. The
 * grid sits below the collection hero, so a page-load animation would otherwise
 * complete before the customer reaches it.
 *
 * @param {HTMLUListElement | null} grid
 */
function prepareInitialMobileJudyReveal(grid) {
  if (!(grid instanceof HTMLElement)) return;
  if (grid.dataset.layout !== 'judy-mobile') return;
  if (grid.dataset.judyInitialRevealBound === 'true') return;

  const items = /** @type {HTMLElement[]} */ (
    Array.from(grid.querySelectorAll(':scope > .product-grid__item')).filter(
      (item) => item instanceof HTMLElement
    )
  );
  if (!items.length) return;

  grid.dataset.judyInitialRevealBound = 'true';
  items.forEach((item) => item.classList.add('judy-grid-item--appended'));
  revealJudyAppendedItems(grid, items);
}

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #isRenderingNext = false;

  /** @type {boolean} */
  #isRenderingPrevious = false;

  /** @type {Set<number>} */
  #fetchingPages = new Set();

  /** @type {number} */
  #desktopLoadingStartedAt = 0;

  connectedCallback() {
    super.connectedCallback();

    if (this.#normalizeMobileInitialPage()) return;

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#observeViewMore();

    requestAnimationFrame(() => {
      if (this.isConnected) {
        this.#attachViewMoreObserver();
        this.#maybePrefetchActiveGrid();
        prepareInitialMobileJudyReveal(this.#getActiveGrid());
      }
    });

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    document.addEventListener('judy:collection-grid-updated', this.#handleCollectionGridUpdated);
    document.addEventListener('judy:collection-mobile-grid-updated', this.#handleCollectionGridUpdated);
    MOBILE_COLLECTION_MQ.addEventListener('change', this.#handleViewportChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    document.removeEventListener('judy:collection-grid-updated', this.#handleCollectionGridUpdated);
    document.removeEventListener('judy:collection-mobile-grid-updated', this.#handleCollectionGridUpdated);
    MOBILE_COLLECTION_MQ.removeEventListener('change', this.#handleViewportChange);
  }

  #handleCollectionGridUpdated = () => {
    if (!this.isConnected) return;

    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#resetInfinityScrollObserver();
      this.#attachViewMoreObserver();
      this.#maybePrefetchActiveGrid();
    });
  };

  #handleViewportChange = () => {
    if (!this.isConnected) return;

    this.#resetInfinityScrollObserver();
    this.#attachViewMoreObserver();
    prepareInitialMobileJudyReveal(this.#getActiveGrid());
  };

  #resetInfinityScrollObserver() {
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
      this.infinityScrollObserver = undefined;
    }
  }

  #maybePrefetchActiveGrid() {
    const grid = this.#getActiveGrid();
    if (!(grid instanceof HTMLElement)) return;

    const lastPage = Number(grid.dataset.lastPage || '1');
    if (lastPage <= 1) return;

    const sentinel = this.#getScrollSentinel('next');
    if (!(sentinel instanceof HTMLElement)) return;

    const rect = sentinel.getBoundingClientRect();
    const mobileScroller = this.#isMobileJudyGrid()
      ? document.querySelector('[data-judy-collection-scroller]')
      : null;
    const viewportBottom =
      mobileScroller instanceof HTMLElement
        ? mobileScroller.getBoundingClientRect().bottom
        : window.innerHeight;
    const preloadDistance = this.#isMobileJudyGrid() ? 320 : 480;

    if (rect.top <= viewportBottom + preloadDistance) {
      this.#renderNextPage();
    }
  }

  #observeViewMore() {
    this.#attachViewMoreObserver();
  }

  #attachViewMoreObserver() {
    const viewMorePrevious = this.#getScrollSentinel('previous');
    const viewMoreNext = this.#getScrollSentinel('next');

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      const isMobileJudyGrid = this.#isMobileJudyGrid();
      const mobileScroller = isMobileJudyGrid
        ? document.querySelector('[data-judy-collection-scroller]')
        : null;
      const rootMargin = isMobileJudyGrid ? '120px 0px 320px 0px' : '360px 0px 480px 0px';

      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            const ref = entry.target.getAttribute('ref');
            if (ref === 'viewMorePrevious') {
              await this.#renderPreviousPage();
            } else if (ref === 'viewMoreNext') {
              await this.#renderNextPage();
            }
          }
        },
        {
          root: mobileScroller instanceof HTMLElement ? mobileScroller : null,
          rootMargin: rootMargin,
        }
      );
    }

    if (viewMorePrevious && !this.#isMobileJudyGrid()) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @returns {boolean}
   */
  #isMobileJudyGrid() {
    return MOBILE_COLLECTION_MQ.matches && this.hasAttribute('data-judy-mobile-grid');
  }

  /**
   * A desktop infinite-scroll URL can be reloaded later on mobile with
   * `?page=N`. Mobile intentionally does not render a previous-page sentinel,
   * so opening the last page would leave only that page's remaining cards.
   *
   * @returns {boolean}
   */
  #normalizeMobileInitialPage() {
    if (!this.#isMobileJudyGrid()) return false;

    const grid = this.#getActiveGrid();
    if (!(grid instanceof HTMLElement)) return false;

    const loadedPages = this.#getGridCards(grid)
      .map((card) => Number(card.dataset.page || '1'))
      .filter((page) => Number.isFinite(page));
    if (!loadedPages.length || Math.min(...loadedPages) <= 1) return false;

    const url = new URL(window.location.href);
    if (!url.searchParams.has('page')) return false;

    url.searchParams.delete('page');
    try {
      sessionStorage.setItem('judy:collection-mobile-scroll-y', '0');
      sessionStorage.removeItem('judy:collection-mobile-keep-scroll');
    } catch (error) {}
    window.location.replace(url.toString());
    return true;
  }

  /**
   * @param {HTMLUListElement} grid
   * @returns {HTMLElement[]}
   */
  #getGridCards(grid) {
    return [...grid.querySelectorAll(':scope > li.product-grid__item[data-page]')];
  }

  /**
   * @param {HTMLUListElement} grid
   * @param {number} pageNumber
   * @returns {boolean}
   */
  #hasPageInGrid(grid, pageNumber) {
    return grid.querySelector(`:scope > li.product-grid__item[data-page="${pageNumber}"]`) != null;
  }

  /**
   * @param {NodeListOf<Element> | Element[]} items
   * @returns {HTMLElement[]}
   */
  #cloneGridItems(items) {
    return Array.from(items).map((item) => /** @type {HTMLElement} */ (item.cloneNode(true)));
  }

  /**
   * @param {HTMLUListElement} grid
   * @param {HTMLElement[]} items
   * @returns {HTMLElement[]}
   */
  #dedupeGridItems(grid, items) {
    const existingIds = new Set(
      [...grid.querySelectorAll(':scope > li.product-grid__item[data-product-id]')].map(
        (element) => element.getAttribute('data-product-id') || ''
      )
    );

    return items.filter((item) => {
      const productId = item.getAttribute('data-product-id') || '';
      if (!productId) return true;
      if (existingIds.has(productId)) return false;
      existingIds.add(productId);
      return true;
    });
  }

  /**
   * @param {string | undefined} layout
   * @returns {string}
   */
  #getGridItemSelector(layout) {
    if (layout === 'judy') {
      return ':scope > li.product-grid__item, :scope > li.judy-grid__ad, :scope > li.product-grid__item--layout-placeholder';
    }

    if (layout === 'judy-mobile') {
      return ':scope > li.product-grid__item';
    }

    return ':scope > [ref="cards[]"], :scope > .product-grid__item';
  }

  /**
   * @param {HTMLElement | null} sentinel
   */
  #pauseSentinel(sentinel) {
    if (sentinel && this.infinityScrollObserver) {
      this.infinityScrollObserver.unobserve(sentinel);
    }
  }

  /**
   * @param {HTMLElement | null} sentinel
   */
  #resumeSentinel(sentinel) {
    if (!sentinel || !this.infinityScrollObserver) return;

    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.infinityScrollObserver?.observe(sentinel);
    });
  }

  /**
   * @param {boolean} active
   */
  #setMobileGridLoading(active) {
    if (!this.#isMobileJudyGrid()) return;
    document.dispatchEvent(
      new CustomEvent('judy:mobile-grid-loading', {
        detail: { active: active, mode: 'load' },
      })
    );
  }

  /**
   * @param {boolean} active
   */
  #setDesktopGridLoading(active) {
    if (this.#isMobileJudyGrid()) return;

    const grid = this.#getActiveGrid();
    if (!(grid instanceof HTMLElement) || grid.dataset.layout !== 'judy') return;

    const root = grid.closest('.judy-desktop-grid-wrap') || grid.closest('.main-collection-grid');
    const loadingEl = root?.querySelector('[data-judy-desktop-grid-loading]');
    if (!(loadingEl instanceof HTMLElement)) return;

    if (active) {
      this.#desktopLoadingStartedAt = Date.now();
    }

    loadingEl.classList.toggle('is-active', active);
    loadingEl.hidden = !active;
    loadingEl.setAttribute('aria-hidden', active ? 'false' : 'true');
    grid.classList.toggle('is-loading-more', active);
  }

  async #finishDesktopGridLoading() {
    if (this.#isMobileJudyGrid()) return;

    const minDisplayMs = 320;
    const elapsed = Date.now() - (this.#desktopLoadingStartedAt || 0);
    if (elapsed < minDisplayMs) {
      await new Promise((resolve) => {
        window.setTimeout(resolve, minDisplayMs - elapsed);
      });
    }

    await new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });

    this.#setDesktopGridLoading(false);
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {HTMLElement | null}
   */
  #getScrollSentinel(type) {
    const refName = type === 'next' ? 'viewMoreNext' : 'viewMorePrevious';
    const grid = this.#getActiveGrid();

    if (grid instanceof HTMLElement) {
      const root =
        grid.closest('.main-collection-grid') ||
        grid.closest('.judy-collection-mobile-grid-wrap') ||
        grid.parentElement;

      const scoped = root?.querySelector(`[ref="${refName}"]`);
      if (scoped instanceof HTMLElement) {
        return scoped;
      }
    }

    const refElement = this.refs[refName];

    if (refElement instanceof HTMLElement) {
      return refElement;
    }

    return this.querySelector(`[ref="${refName}"]`);
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const grid = this.#getActiveGrid();
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * Judy collection renders desktop + mobile grids; pick the one in use.
   *
   * @returns {HTMLUListElement | null}
   */
  #getActiveGrid() {
    const mobileGrid = this.querySelector('ul[ref="grid"][data-layout="judy-mobile"]');
    const desktopGrid = this.querySelector('ul[ref="grid"][data-layout="judy"]');

    if (MOBILE_COLLECTION_MQ.matches && mobileGrid) {
      return mobileGrid;
    }

    return desktopGrid || this.refs.grid || null;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = getJudyCollectionFetchUrl();
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.searchParams.delete('section_id');
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;

    if (this.#fetchingPages.has(pageNumber)) return;
    this.#fetchingPages.add(pageNumber);

    try {
      const pageContent = await this.#fetchGridPageHtml(pageInfo.url);
      this.pages.set(pageNumber, pageContent);
    } finally {
      this.#fetchingPages.delete(pageNumber);
    }
  }

  /**
   * Fetch paginated grid HTML from the main collection section.
   * @param {URL} pageUrl
   * @returns {Promise<string>}
   */
  async #fetchGridPageHtml(pageUrl) {
    return sectionRenderer.getSectionHTML(this.sectionId, false, pageUrl);
  }

  async #renderNextPage() {
    if (this.#isRenderingNext) return;

    const grid = this.#getActiveGrid();
    if (!grid) return;

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    if (this.#hasPageInGrid(grid, nextPage.page)) return;

    const viewMoreNext = this.#getScrollSentinel('next');
    this.#isRenderingNext = true;
    this.#pauseSentinel(viewMoreNext);
    this.#setMobileGridLoading(true);
    this.#setDesktopGridLoading(true);

    try {
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements?.length) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements?.length) return;
      }

      const clonedItems = this.#cloneGridItems(nextPageItemElements);
      const newItems = this.#dedupeGridItems(grid, clonedItems);
      if (!newItems.length) return;

      if (grid.dataset.layout === 'judy' || grid.dataset.layout === 'judy-mobile') {
        newItems.forEach((item) => {
          item.classList.add('judy-grid-item--appended');
        });
      }

      grid.append(...newItems);

      this.#aspectRatioHelper?.processNewElements();

      if (grid.dataset.layout === 'judy' || grid.dataset.layout === 'judy-mobile') {
        revealJudyAppendedItems(grid, newItems);
      }

      if (grid.dataset.layout === 'judy') {
        document.dispatchEvent(new CustomEvent('judy:collection-grid-updated'));
      }

      if (grid.dataset.layout === 'judy-mobile') {
        document.dispatchEvent(new CustomEvent('judy:collection-mobile-grid-updated'));
      }

      if (!this.#isMobileJudyGrid()) {
        history.pushState('', '', nextPage.url.toString());
      }

      this.#prefetchAdjacentPageIfNearViewport('next');
    } finally {
      this.#isRenderingNext = false;
      this.#setMobileGridLoading(false);

      if (!this.#isMobileJudyGrid() && grid instanceof HTMLElement && grid.dataset.layout === 'judy') {
        await this.#finishDesktopGridLoading();
      }

      this.#resumeSentinel(viewMoreNext);
    }
  }

  async #renderPreviousPage() {
    const grid = this.#getActiveGrid();
    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements?.length) return;
    }

    if (!previousPageItemElements.length) return;

    const clonedItems = this.#cloneGridItems(previousPageItemElements);

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...clonedItems);

    this.#aspectRatioHelper?.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    this.#prefetchAdjacentPageIfNearViewport('previous');
  }

  /**
   * Prefetch the next/previous page only while the sentinel is still near the viewport.
   *
   * @param {"previous" | "next"} type
   */
  #prefetchAdjacentPageIfNearViewport(type) {
    const sentinel = this.#getScrollSentinel(type);
    if (!(sentinel instanceof Element)) return;

    requestIdleCallback(() => {
      if (!this.isConnected) return;

      const rect = sentinel.getBoundingClientRect();
      const mobileScroller = this.#isMobileJudyGrid()
        ? document.querySelector('[data-judy-collection-scroller]')
        : null;
      const viewportBottom =
        mobileScroller instanceof HTMLElement
          ? mobileScroller.getBoundingClientRect().bottom
          : window.innerHeight;
      const nearViewport = rect.top < viewportBottom + (this.#isMobileJudyGrid() ? 320 : 480);
      if (!nearViewport) return;

      this.#fetchPage(type);
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const grid = this.#getActiveGrid();
    const isPrevious = type === 'previous';

    if (!grid) return;

    const cards = this.#getGridCards(grid);
    if (!cards.length) return;

    let page;

    if (isPrevious) {
      const targetCard = cards[0];
      const currentCardPage = Number(targetCard.dataset.page);
      page = currentCardPage - 1;
    } else {
      let maxPage = 0;
      for (const card of cards) {
        maxPage = Math.max(maxPage, Number(card.dataset.page) || 0);
      }
      page = maxPage + 1;
    }

    const url = getJudyCollectionFetchUrl();
    url.searchParams.set('page', page.toString());
    url.searchParams.delete('section_id');
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const activeLayout = this.#getActiveGrid()?.dataset.layout;
    let gridElement = null;

    if (activeLayout === 'judy') {
      gridElement = parsedPage.querySelector('[ref="grid"][data-layout="judy"]');
    }

    if (!gridElement && activeLayout) {
      gridElement = parsedPage.querySelector(`[ref="grid"][data-layout="${activeLayout}"]`);
    }

    if (!gridElement) {
      gridElement = parsedPage.querySelector('[ref="grid"]');
    }

    if (!gridElement) return;

    const itemSelector = this.#getGridItemSelector(gridElement.dataset.layout);
    const items = gridElement.querySelectorAll(itemSelector);
    return items.length ? items : undefined;
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;
    this.#fetchingPages.clear();

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.#getActiveGrid()?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.#getActiveGrid()?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#resetInfinityScrollObserver();
        this.#observeViewMore();
        this.#maybePrefetchActiveGrid();
      }
    });

    // Observe the grid for changes
    const grid = this.#getActiveGrid();
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
